import { supabase } from '../lib/supabase'

/**
 * Get all theme colours from the database
 * @returns {Promise<{data: Array<{category: string, hex_value: string, description: string, updated_at: string}> | null, error: Error | null}>}
 */
export const getThemeConfig = async () => {
  try {
    const { data, error } = await supabase
      .rpc('get_theme_config')

    if (error) throw error
    return { data, error: null }
  } catch (error) {
    console.error('Error fetching theme config:', error)
    return { data: null, error }
  }
}

/**
 * Update theme colours (super admin only)
 * @param {Array<{category: string, hex_value: string}>} colours - Array of colour updates
 * @returns {Promise<{data: Array | null, error: Error | null}>}
 */
export const updateThemeConfig = async (colours) => {
  try {
    // Validate colours array
    if (!Array.isArray(colours) || colours.length === 0) {
      throw new Error('Colours must be a non-empty array')
    }

    // Validate each colour has required fields
    for (const colour of colours) {
      if (!colour.category || !colour.hex_value) {
        throw new Error('Each colour must have category and hex_value')
      }
      // Validate hex format
      if (!/^#[A-Fa-f0-9]{6}$/.test(colour.hex_value)) {
        throw new Error(`Invalid hex colour format: ${colour.hex_value}. Expected format: #RRGGBB`)
      }
    }

    const { data, error } = await supabase
      .rpc('update_theme_config', { p_colours: colours })

    if (error) throw error
    return { data, error: null }
  } catch (error) {
    console.error('Error updating theme config:', error)
    return { data: null, error }
  }
}

/**
 * Reset theme colours to defaults (super admin only)
 * @returns {Promise<{data: Array | null, error: Error | null}>}
 */
export const resetThemeConfig = async () => {
  try {
    const { data, error } = await supabase
      .rpc('reset_theme_config')

    if (error) throw error
    return { data, error: null }
  } catch (error) {
    console.error('Error resetting theme config:', error)
    return { data: null, error }
  }
}

/**
 * Default theme colours (used as fallback if database fetch fails)
 */
export const DEFAULT_THEME_COLOURS = [
  { category: 'primary', hex_value: '#2F8F5B', description: 'Main brand colour for primary buttons and key interactive elements' },
  { category: 'primary_hover', hex_value: '#277A4C', description: 'Hover state for primary elements' },
  { category: 'primary_active', hex_value: '#1F6640', description: 'Active/pressed state for primary elements' },
  { category: 'secondary', hex_value: '#44403C', description: 'Secondary buttons and less prominent actions' },
  { category: 'accent', hex_value: '#2F8F5B', description: 'Links, info icons, and highlights' },
  { category: 'success', hex_value: '#2F8F5B', description: 'Success states and confirmations' },
  { category: 'warning', hex_value: '#CA8A04', description: 'Warning states and caution indicators' },
  { category: 'error', hex_value: '#DC2626', description: 'Error states, danger buttons, validation errors' },
  { category: 'background', hex_value: '#FAFAF9', description: 'Main page backgrounds' },
  { category: 'surface', hex_value: '#FFFFFF', description: 'Card backgrounds and elevated surfaces' },
  { category: 'border', hex_value: '#E7E5E4', description: 'Default borders' },
  { category: 'text_primary', hex_value: '#1C1917', description: 'Main body text' },
  { category: 'text_secondary', hex_value: '#44403C', description: 'Muted/secondary text' },
  { category: 'text_disabled', hex_value: '#78716C', description: 'Disabled state text' },
  { category: 'text_inverse', hex_value: '#FFFFFF', description: 'Text on dark or coloured backgrounds' }
]

/**
 * Format category name for display (e.g., 'primary_hover' -> 'Primary Hover')
 * @param {string} category - The category key
 * @returns {string} Formatted display name
 */
export const formatCategoryName = (category) => {
  return category
    .split('_')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ')
}

/**
 * Validate a hex colour string
 * @param {string} hex - The hex colour to validate
 * @returns {boolean} True if valid hex colour
 */
export const isValidHexColour = (hex) => {
  return /^#[A-Fa-f0-9]{6}$/.test(hex)
}

/**
 * Normalise hex colour to uppercase 6-character format
 * @param {string} hex - The hex colour (can be 3 or 6 chars, with or without #)
 * @returns {string} Normalised hex colour (#RRGGBB format)
 */
export const normaliseHexColour = (hex) => {
  // Remove # if present
  let cleaned = hex.replace('#', '').toUpperCase()

  // Expand 3-char to 6-char
  if (cleaned.length === 3) {
    cleaned = cleaned.split('').map(c => c + c).join('')
  }

  return `#${cleaned}`
}
